import { Middleware, MiddlewareAPI } from '@reduxjs/toolkit'
import { io, Socket } from 'socket.io-client'

import { api } from './api/api'
import { slice as authSlice } from './auth'
import { slice as marketSlice } from './market'

// В socket.io путь в URL — это namespace, а не часть адреса. Гейтвей namespace
// не объявляет, поэтому существует только '/', и с хвостом '/api' подключение
// падало с «Invalid namespace». Адрес сервера берём без него, а /api уходит в path.
const WS_URL = import.meta.env.VITE_BASE_URL
  ?.replace(/\/api\/?$/, '')
  .replace('https://', 'wss://')
  .replace('http://', 'ws://')

let connection: Socket | null = null

/**
 * Поднять соединение, если его ещё нет.
 *
 * ⚠️ ЗОВЁТСЯ И ПОСЛЕ ВХОДА, И ПОСЛЕ ВОССТАНОВЛЕНИЯ СЕССИИ — и второе тут
 * главное. Раньше сокет создавался ТОЛЬКО по `loginSuccess`, а шлёт его
 * единственное место — форма входа (`components/Login.tsx`). При
 * перезагрузке страницы с сохранённым токеном путь другой: стор
 * поднимается сразу в `checking`, дальше `getMe` → `userLoadSuccess` →
 * `loggedIn`, и `loginSuccess` не диспатчится никогда. То есть **после
 * любого F5 раннер оставался без живых обновлений до следующего входа**,
 * и ничем это не показывалось: экран выглядел рабочим, просто заказы
 * переставали приходить сами.
 *
 * ⚠️ Токен берётся ИЗ СТОРА, а не из payload действия. Payload есть только
 * у `loginSuccess`; читая стор, обе точки входа получают токен одинаково,
 * и третья, если появится, тоже. `next(action)` к этому моменту уже
 * отработал, так что в сторе лежит свежий токен.
 *
 * Зачем токен: гейтвей с задачи `s-3` опознаёт клиента и считает, сколько
 * подключений пришло без токена. Пока это число не ноль, включить жёсткую
 * проверку доступа нельзя — сегодня любой, кто открыл сокет, подписывается
 * на чужую точку.
 */
const ensureConnection = (store: MiddlewareAPI) => {
  if (connection) {
    return
  }

  const token = store.getState().auth?.accessToken

  connection = io(WS_URL, {
    path: '/api/pusher',
    transports: ['websocket', 'polling'],
    autoConnect: false,
    auth: token ? { token } : {},
  })

  connection.on('action', (event: { type: string; data: unknown }) => {
    if (event.type === 'SOCKET/CONNECTED') {
      const { selected } = store.getState().market
      if (selected) {
        connection!.emit('action', { type: 'SOCKET/SET_RUNNER', payload: { market: selected } })
      }
    }

    // Invalidate orders cache on new order
    if (event.type === 'SOCKET/NEW_ORDER') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      store.dispatch(api.util.invalidateTags([{ type: 'Orders' }] as any))
    }

    // Invalidate orders cache on order change.
    // Помимо списка обновляем и открытую карточку заказа: старый эпик catchOrderChange
    // перезагружал текущий заказ. Payload несёт id аутлет-заказа, а не родительского,
    // поэтому точечно инвалидируем те getOrder, что реально лежат в кэше (обычно —
    // открытый detail-экран), а не пытаемся вывести id из payload.
    if (event.type === 'SOCKET/CHANGE_ORDER') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tags: any[] = [{ type: 'Orders' }]
      const queries = store.getState().api?.queries ?? {}
      Object.values(queries).forEach((entry) => {
        const query = entry as { endpointName?: string; originalArgs?: unknown }
        if (query?.endpointName === 'getOrder' && typeof query.originalArgs === 'number') {
          tags.push({ type: 'Order', id: query.originalArgs })
        }
      })
      store.dispatch(api.util.invalidateTags(tags))
    }
  })

  connection.connect()
}

export const socketMiddleware: Middleware = (store) => (next) => (action) => {
  const result = next(action)

  // Соединение поднимается и после входа, и после восстановления сессии
  if (
    authSlice.actions.loginSuccess.match(action) ||
    authSlice.actions.userLoadSuccess.match(action)
  ) {
    ensureConnection(store)
  }

  // Register runner on market selection
  if (marketSlice.actions.selectMarket.match(action)) {
    if (connection?.connected) {
      connection.emit('action', { type: 'SOCKET/SET_RUNNER', payload: { market: action.payload } })
    }
  }

  // Disconnect socket on logout and reset
  if (authSlice.actions.reset.match(action)) {
    if (connection) {
      connection.disconnect()
      connection = null
    }
  }

  return result
}
